// src/hooks/useTicketsLogic.js
import { useState, useEffect, useMemo } from "react";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  where,
  runTransaction,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase";
import { showCustomToast } from "../misc/CustomToast";
import { useAuth } from "../context/AuthContext";

export default function useTicketsLogic(
  urlProjectId,
  urlTicketId,
  translate,
  navigate
) {
  const { currentUser } = useAuth();
  const [role, setRole] = useState("Normal User");
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [accessibleProjects, setAccessibleProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(urlProjectId || null);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: "",
    priority: "",
    dateAdded: "",
    ticketFilters: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const viewType = "list";
  const [prioritySort, setPrioritySort] = useState(null);
  const [openAddProjectModal, setOpenAddProjectModal] = useState(false);
  const [openAddTicketModal, setOpenAddTicketModal] = useState(false);
  const [openEditTicketModal, setOpenEditTicketModal] = useState(false);
  const [openDeleteTicketModal, setOpenDeleteTicketModal] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [ticketToEdit, setTicketToEdit] = useState(null);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const statusTabs = [
    { key: "All", labelKey: "All" },
    { key: "Open", labelKey: "Open" },
    { key: "Working on It", labelKey: "WorkingOnIt" },
    { key: "In Staging", labelKey: "InStaging" },
    { key: "In Test", labelKey: "InTest" },
    { key: "In Review", labelKey: "InReview" },
    { key: "In Production", labelKey: "InProduction" },
    { key: "Closed", labelKey: "Closed" },
  ];

  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role || "Normal User");
      setCurrentUserProfile({ id: snap.id, ...data });
    });
    return unsub;
  }, [currentUser]);

  useEffect(() => {
    if (!currentUserProfile?.companyId) return;
    const qUsers = query(
      collection(db, "profilsUsers"),
      where("companyId", "==", currentUserProfile.companyId)
    );
    const unsubUsers = onSnapshot(qUsers, (snap) => {
      setCompanyUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const qProjects = query(
      collection(db, "projects"),
      where("companyId", "==", currentUserProfile.companyId)
    );
    const unsubProjects = onSnapshot(qProjects, (snap) => {
      setAllProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => {
      unsubUsers();
      unsubProjects();
    };
  }, [currentUserProfile]);

  useEffect(() => {
    if (!currentUser) return;
    if (role === "Admin") {
      setAccessibleProjects(allProjects);
    } else {
      setAccessibleProjects(
        allProjects.filter(
          (p) =>
            Array.isArray(p.memberIds) && p.memberIds.includes(currentUser.uid)
        )
      );
    }
  }, [allProjects, currentUser, role]);

  useEffect(() => {
    if (urlProjectId && urlProjectId !== selectedProject) {
      if (accessibleProjects.some((p) => p.id === urlProjectId)) {
        setSelectedProject(urlProjectId);
        navigate(`/app/projects/${encodeURIComponent(urlProjectId)}`, {
          replace: true,
        });
      }
    }
  }, [urlProjectId, accessibleProjects]);

  const handleProjectChange = (id) => {
    setSelectedProject(id);
    navigate(`/app/projects/${encodeURIComponent(id)}`, { replace: true });
  };

  useEffect(() => {
    if (!currentUserProfile?.companyId || !selectedProject) return;
    setTicketsLoading(true);
    let qTickets;
    if (role === "Admin" && selectedProject === "__all") {
      qTickets = query(
        collectionGroup(db, "tickets"),
        where("companyId", "==", currentUserProfile.companyId)
      );
    } else {
      qTickets = query(collection(db, "projects", selectedProject, "tickets"));
    }
    const unsub = onSnapshot(qTickets, (snap) => {
      setTickets(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => b.createdAt.seconds - a.createdAt.seconds)
      );
      setTicketsLoading(false);
    });
    return unsub;
  }, [currentUserProfile, selectedProject, role]);

  useEffect(() => {
    if (!urlTicketId) return;
    const decoded = decodeURIComponent(urlTicketId);
    const t = tickets.find((t) => t.id === decoded);
    if (t) {
      setTicketToEdit(t);
      setOpenEditTicketModal(true);
    }
  }, [tickets, urlTicketId]);

  const statusCounts = useMemo(() => {
    const counts = { All: tickets.length };
    statusTabs.forEach(({ key }) => {
      if (key !== "All") {
        counts[key] = tickets.filter((t) => t.status === key).length;
      }
    });
    return counts;
  }, [tickets]);

  const displayedTickets = useMemo(() => {
    const { key } = statusTabs[tabIndex];
    let list =
      key === "All" ? tickets.slice() : tickets.filter((t) => t.status === key);
    if (prioritySort) {
      const order = { High: 3, Medium: 2, Low: 1 };
      list.sort((a, b) =>
        prioritySort === "asc"
          ? order[a.priority] - order[b.priority]
          : order[b.priority] - order[a.priority]
      );
    }
    return list.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
  }, [tickets, statusTabs, tabIndex, prioritySort]);

  const handleOpenAddProjectModal = () => setOpenAddProjectModal(true);
  const handleOpenAddTicketModal = () => setOpenAddTicketModal(true);
  const handleCloseAddTicketModal = () => setOpenAddTicketModal(false);
  const handleOpenEditModal = (ticket) => {
    setTicketToEdit(ticket);
    setOpenEditTicketModal(true);
    navigate(`/app/projects/${selectedProject}/tickets/${ticket.id}`, {
      replace: true,
    });
  };
  const handleCloseEditModal = () => {
    setTicketToEdit(null);
    setOpenEditTicketModal(false);
    navigate(`/app/projects/${selectedProject}`, { replace: true });
  };
  const handleOpenDeleteModal = (id) => {
    setTicketToDelete(id);
    setOpenDeleteTicketModal(true);
  };
  const handleCloseDeleteModal = () => {
    setTicketToDelete(null);
    setOpenDeleteTicketModal(false);
  };
  const handleDeleteTicket = async (id) => {
    await updateDoc(doc(db, "projects", selectedProject, "tickets", id), {
      deleted: true,
    });
    setOpenDeleteTicketModal(false);
  };
  const handleCreateProject = async (
    newProjectName,
    newPageListCategories,
    selectedMembers
  ) => {
    if (!newProjectName.trim()) return;
    const id = newProjectName.trim();
    const refP = doc(db, "projects", id);
    const snap = await getDoc(refP);
    if (snap.exists()) {
      showCustomToast(translate("ProjectAlreadyExists"), "error");
      return;
    }
    const proj = {
      name: id,
      companyId: currentUserProfile.companyId,
      companyName: currentUserProfile.companyName,
      pageListCategories: newPageListCategories,
      createdAt: new Date(),
      memberIds: [currentUser.uid, ...selectedMembers.map((u) => u.id)],
      memberEmails: [currentUser.email, ...selectedMembers.map((u) => u.email)],
    };
    await setDoc(refP, proj);
    await setDoc(doc(db, "projects", id, "meta", "counter"), {
      nextTicketNumber: 0,
    });
    setOpenAddProjectModal(false);
  };
  const handleSubmitTicket = async ({
    ticketName,
    originalQuestion,
    ticketType,
    pageList,
    selectedPriority,
    attachments,
  }) => {
    if (!selectedProject) {
      showCustomToast(translate("SelectProject"), "error");
      return;
    }
    setLoading(true);
    try {
      const counterRef = doc(db, "meta", "ticketCounter");
      let generatedNumber;
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const next = (snap.exists() ? snap.data().nextTicketNumber : 0) + 1;
        tx.set(counterRef, { nextTicketNumber: next }, { merge: true });
        const prefix = selectedProject.toUpperCase().slice(0, 4);
        generatedNumber = `${prefix}-${String(next).padStart(6, "0")}`;
      });
      const newTicket = {
        ticketName,
        ticketType,
        originalQuestion,
        pageList,
        priority: selectedPriority,
        createdBy: currentUser.displayName || "",
        createdById: currentUser.uid,
        createdAt: new Date(),
        status: "Open",
        ticketNumber: generatedNumber,
        companyId: currentUserProfile.companyId,
        companyName: currentUserProfile.companyName,
        projectId: selectedProject,
      };
      const ticketRef = await addDoc(
        collection(db, "projects", selectedProject, "tickets"),
        newTicket
      );
      const imageUrls = [];
      for (const { file } of attachments) {
        const path = `tickets/${ticketRef.id}/${file.name}`;
        const snap = await uploadBytes(storageRef(storage, path), file);
        imageUrls.push(await getDownloadURL(snap.ref));
      }
      if (imageUrls.length) {
        await updateDoc(ticketRef, { images: imageUrls });
      }
      await addDoc(
        collection(
          db,
          "projects",
          selectedProject,
          "tickets",
          ticketRef.id,
          "activity"
        ),
        {
          action: "Ticket créé",
          performedBy: currentUser.displayName,
          timestamp: new Date(),
        }
      );
      setOpenAddTicketModal(false);
    } catch {
      showCustomToast(translate("FailedToAddTicket"), "error");
    } finally {
      setLoading(false);
    }
  };
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const newStatus = destination.droppableId;
    setTickets((prev) =>
      prev.map((t) => (t.id === draggableId ? { ...t, status: newStatus } : t))
    );
    await updateDoc(
      doc(db, "projects", selectedProject, "tickets", draggableId),
      { status: newStatus }
    );
  };
  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handleCheckboxChange = (id) => {
    setCompanyUsers((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return {
    role,
    currentUser,
    currentUserProfile,
    companyUsers,
    accessibleProjects,
    selectedProject,
    tickets,
    ticketsLoading,
    displayedTickets,
    statusTabs,
    statusCounts,
    tabIndex,
    viewType,
    filters,
    attachments,
    openAddProjectModal,
    openAddTicketModal,
    openEditTicketModal,
    openDeleteTicketModal,
    openUploadModal,
    ticketToEdit,
    ticketToDelete,
    loading,
    editLoading,
    prioritySort,
    setTabIndex,
    setPrioritySort,
    handleProjectChange,
    handleOpenAddProjectModal,
    handleOpenAddTicketModal,
    handleCloseAddTicketModal,
    handleOpenEditModal,
    handleCloseEditModal,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeleteTicket,
    handleCreateProject,
    handleSubmitTicket,
    handleDragEnd,
    handleFilterChange,
    handleCheckboxChange,
    setAttachments,
  };
}
